import { rangeOverlap } from '~/services/TimeService';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import { daysOfWeek } from './daysOfWeek';

interface ConflictResult {
  /** Date ranges overlap and at least one weekday is shared */
  hasConflict: boolean;
  /** Both surcharges have accumulate: true — allowed without extra confirmation */
  canAccumulate: boolean;
  /** Overlap with one accumulating and one non-accumulating — requires confirmation */
  mixedAccumulate: boolean;
}

export function analyzeSurchargePair(a: SalesforceSurchargePeriod, b: SalesforceSurchargePeriod): ConflictResult {
  const rangeConflict = rangeOverlap(a.start_date, a.end_date, b.start_date, b.end_date);

  const daysConflict = daysOfWeek.some((d) => {
    return a[d.key] && b[d.key];
  });

  const hasConflict = rangeConflict && daysConflict;

  if (!hasConflict) {
    return { hasConflict: false, canAccumulate: false, mixedAccumulate: false };
  }

  const canAccumulate = !!a.accumulate && !!b.accumulate;
  const mixedAccumulate = !!a.accumulate !== !!b.accumulate;

  return {
    hasConflict: true,
    canAccumulate,
    mixedAccumulate,
  };
}

function allPairs<T>(arr: Array<T>): Array<[T, T]> {
  return arr.flatMap((a, i) => arr.slice(i + 1).map((b) => [a, b] as [T, T]));
}

/**
 * True when some pair of selected surcharges overlaps (date + weekday) and mixing
 * accumulate / non-accumulate requires user confirmation before import.
 */
export function hasMixedAccumulateOverlap(surcharges: Array<SalesforceSurchargePeriod>): boolean {
  return allPairs(surcharges).some(([a, b]) => analyzeSurchargePair(a, b).mixedAccumulate);
}

/** Surcharges that overlap (date + weekday) but neither has the `overlapping` flag set in Salesforce */
export function getUnmarkedOverlappingSurcharges(
  surcharges: Array<SalesforceSurchargePeriod>,
): Array<SalesforceSurchargePeriod> {
  const ids = new Set(
    allPairs(surcharges)
      .filter(([a, b]) => analyzeSurchargePair(a, b).hasConflict && !a.overlapping && !b.overlapping)
      .flat()
      .map((s) => s.id),
  );
  return surcharges.filter((s) => ids.has(s.id));
}

/** Returns each distinct pair of surcharges that overlap with mixed accumulate flags */
export function getMixedAccumulateConflictPairs(
  surcharges: Array<SalesforceSurchargePeriod>,
): Array<[SalesforceSurchargePeriod, SalesforceSurchargePeriod]> {
  return allPairs(surcharges).filter(([a, b]) => analyzeSurchargePair(a, b).mixedAccumulate);
}
